import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Users, ShoppingBag, DollarSign, Clock, Eye, Pencil, Trash2, 
  Plus, X, Save, Image as ImageIcon, Upload, ChevronDown, Check, AlertCircle,
  Truck, Calendar, Hash, Search, Filter, Send, Phone, MapPin, Copy, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'yeezy-350-v2', label: 'Yeezy 350 V2' },
  { value: 'yeezy-380', label: 'Yeezy 380' },
  { value: 'yeezy-500', label: 'Yeezy 500' },
  { value: 'yeezy-700', label: 'Yeezy 700' },
  { value: 'louboutin', label: 'Louboutin' },
  { value: 'alexander-mcqueen', label: 'Alexander McQueen' },
];

// Standard sizes for non-Yeezy products
const STANDARD_SIZES = ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'];

// Yeezy-specific sizes (adidas fractional sizing)
const YEEZY_SIZES = [
  '36', '36 2/3', '37 1/3', '38', '38 2/3', '39 1/3', 
  '40', '40 2/3', '41 1/3', '42', '42 2/3', '43 1/3', 
  '44', '44 2/3', '45 1/3', '46', '46 2/3', '47 1/3'
];

// Helper to check if category is Yeezy
const isYeezyCategory = (category) => {
  return category?.toLowerCase().includes('yeezy');
};

// Helper to get sizes based on category
const getSizesForCategory = (category) => {
  return isYeezyCategory(category) ? YEEZY_SIZES : STANDARD_SIZES;
};

const BADGES = ['none', 'BEST SELLER', 'NEW', 'LIMITED', 'SALE'];

const COURIERS = [
  { value: 'Fan Courier', label: 'Fan Courier' },
  { value: 'Sameday', label: 'Sameday' },
  { value: 'Cargus', label: 'Cargus' },
  { value: 'Other', label: 'Altul' },
];

const ORDER_STATUSES = [
  { value: 'all', label: 'Toate', color: 'bg-white/10 text-white' },
  { value: 'pending', label: 'În Așteptare', color: 'bg-yellow-500/20 text-yellow-500' },
  { value: 'confirmed', label: 'Confirmate', color: 'bg-blue-500/20 text-blue-500' },
  { value: 'shipped', label: 'Expediate', color: 'bg-cyan-500/20 text-cyan-500' },
  { value: 'delivered', label: 'Livrate', color: 'bg-green-500/20 text-green-500' },
];

const emptyProduct = {
  name: '',
  slug: '',
  category: 'yeezy-350-v2',
  price: 0,
  original_price: 0,
  description: '',
  images: [''],
  sizes: ['40', '40 2/3', '41 1/3', '42', '42 2/3'],
  colors: ['White', 'Black'],
  badge: 'none',
  is_reflective: false,
  featured: false,
  in_stock: true,
};

// Recommended Products Manager Component
const RecommendedProductsManager = ({ products, token, onUpdate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(null);

  const recommendedProducts = products.filter(p => p.is_recommended);
  const availableProducts = products.filter(p => !p.is_recommended);

  const filteredAvailable = availableProducts.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleRecommended = async (productId, isRecommended) => {
    setSaving(productId);
    try {
      await axios.put(
        `${API}/admin/products/${productId}/recommended?is_recommended=${isRecommended}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isRecommended ? 'Produs adăugat la recomandate' : 'Produs eliminat din recomandate');
      onUpdate();
    } catch (error) {
      toast.error('Eroare la actualizare');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Currently Recommended */}
      <div>
        <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-[#c00000]" />
          PRODUSE RECOMANDATE ACTIVE ({recommendedProducts.length})
        </h3>
        {recommendedProducts.length === 0 ? (
          <div className="bg-[#121212] border border-white/5 p-8 text-center">
            <p className="text-white/50 font-body">Niciun produs recomandat selectat.</p>
            <p className="text-white/30 font-body text-sm mt-2">Adaugă produse din lista de mai jos.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {recommendedProducts.map(product => (
              <div key={product.id} className="bg-[#121212] border border-[#c00000]/30 p-4">
                <div className="aspect-square bg-[#0a0a0a] mb-3 overflow-hidden relative">
                  <img 
                    src={product.images?.[0] || 'https://via.placeholder.com/400'} 
                    alt={product.name} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-[#c00000] text-white text-[10px] font-bold px-2 py-1">
                    RECOMANDAT
                  </div>
                </div>
                <h4 className="font-heading text-sm text-white truncate">{product.name}</h4>
                <p className="text-white/50 text-xs mb-3">{product.category}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleRecommended(product.id, false)}
                  disabled={saving === product.id}
                  className="w-full border-red-500/50 text-red-500 hover:bg-red-500/10"
                  data-testid={`remove-recommended-${product.id}`}
                >
                  {saving === product.id ? (
                    <div className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Elimină
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Products */}
      <div>
        <h3 className="font-heading text-lg text-white mb-4">ADAUGĂ PRODUSE LA RECOMANDATE</h3>
        
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            type="text"
            placeholder="Caută produse..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white"
            data-testid="recommended-search-input"
          />
        </div>

        {/* Available Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto">
          {filteredAvailable.map(product => (
            <div key={product.id} className="bg-[#121212] border border-white/5 p-4 hover:border-white/20 transition-colors">
              <div className="aspect-square bg-[#0a0a0a] mb-3 overflow-hidden">
                <img 
                  src={product.images?.[0] || 'https://via.placeholder.com/400'} 
                  alt={product.name} 
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-heading text-sm text-white truncate">{product.name}</h4>
              <p className="text-white/50 text-xs mb-3">{product.category}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleRecommended(product.id, true)}
                disabled={saving === product.id}
                className="w-full border-[#c00000]/50 text-[#c00000] hover:bg-[#c00000]/10"
                data-testid={`add-recommended-${product.id}`}
              >
                {saving === product.id ? (
                  <div className="w-4 h-4 border-2 border-[#c00000]/30 border-t-[#c00000] rounded-full animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        {filteredAvailable.length === 0 && (
          <div className="bg-[#121212] border border-white/5 p-8 text-center">
            <p className="text-white/50 font-body">
              {searchQuery ? 'Niciun produs găsit.' : 'Toate produsele sunt deja recomandate.'}
            </p>
          </div>
        )}

        <p className="text-xs text-white/30 mt-4 text-right">
          {filteredAvailable.length} produse disponibile
        </p>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Product modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  
  // Order detail modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Order tracking update state
  const [trackingForm, setTrackingForm] = useState({
    order_status: '',
    courier: '',
    awb: '',
    estimated_delivery: ''
  });
  const [updatingOrder, setUpdatingOrder] = useState(false);
  
  // Orders filters and search
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  
  // Quick ship modal
  const [quickShipOrder, setQuickShipOrder] = useState(null);
  const [quickShipForm, setQuickShipForm] = useState({ courier: '', awb: '', customCourier: '' });
  const [quickShipping, setQuickShipping] = useState(false);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    // Status filter
    if (orderStatusFilter !== 'all') {
      result = result.filter(o => o.order_status === orderStatusFilter);
    }
    
    // Search filter
    if (orderSearch.trim()) {
      const search = orderSearch.toLowerCase().trim();
      result = result.filter(o => 
        (o.order_number?.toLowerCase().includes(search)) ||
        (o.id?.toLowerCase().includes(search)) ||
        (o.address?.full_name?.toLowerCase().includes(search)) ||
        (o.address?.phone?.includes(search)) ||
        (o.awb?.toLowerCase().includes(search))
      );
    }
    
    // Sort by date (newest first)
    result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return result;
  }, [orders, orderStatusFilter, orderSearch]);
  
  // Order counts by status
  const orderCounts = useMemo(() => {
    return {
      all: orders.length,
      pending: orders.filter(o => o.order_status === 'pending').length,
      confirmed: orders.filter(o => o.order_status === 'confirmed').length,
      shipped: orders.filter(o => o.order_status === 'shipped').length,
      delivered: orders.filter(o => o.order_status === 'delivered').length,
    };
  }, [orders]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated || !isAdmin) {
      navigate('/login');
      return;
    }

    fetchData();
  }, [isAuthenticated, isAdmin, authLoading, navigate, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, ordersRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/products`, { headers })
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, 
        { order_status: status },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setOrders(orders.map(o => o.id === orderId ? { ...o, order_status: status } : o));
      toast.success('Status actualizat');
    } catch (error) {
      toast.error('Eroare la actualizare status');
    }
  };

  const openOrderWithTracking = (order) => {
    setSelectedOrder(order);
    setTrackingForm({
      order_status: order.order_status || 'pending',
      courier: order.courier || '',
      awb: order.awb || '',
      estimated_delivery: order.estimated_delivery || ''
    });
  };

  const updateOrderWithTracking = async () => {
    if (!selectedOrder) return;
    
    setUpdatingOrder(true);
    try {
      await axios.put(`${API}/admin/orders/${selectedOrder.id}/status`, 
        trackingForm,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local state
      setOrders(orders.map(o => o.id === selectedOrder.id ? { 
        ...o, 
        order_status: trackingForm.order_status,
        courier: trackingForm.courier,
        awb: trackingForm.awb,
        estimated_delivery: trackingForm.estimated_delivery
      } : o));
      
      setSelectedOrder(prev => ({
        ...prev,
        order_status: trackingForm.order_status,
        courier: trackingForm.courier,
        awb: trackingForm.awb,
        estimated_delivery: trackingForm.estimated_delivery
      }));
      
      toast.success('Comandă actualizată cu succes');
      
      // If status is shipped and AWB provided, email will be sent automatically
      if (trackingForm.order_status === 'shipped' && trackingForm.awb) {
        toast.success('Email de expediere trimis către client');
      }
    } catch (error) {
      toast.error('Eroare la actualizarea comenzii');
    } finally {
      setUpdatingOrder(false);
    }
  };

  const openQuickShip = (order) => {
    setQuickShipOrder(order);
    setQuickShipForm({ 
      courier: order.courier || '', 
      awb: order.awb || '',
      customCourier: ''
    });
  };

  const handleQuickShip = async () => {
    if (!quickShipOrder) return;
    
    const courierValue = quickShipForm.courier === 'Other' ? quickShipForm.customCourier : quickShipForm.courier;
    
    if (!courierValue || !quickShipForm.awb) {
      toast.error('Te rugăm să completezi curier și AWB');
      return;
    }
    
    setQuickShipping(true);
    try {
      await axios.put(`${API}/admin/orders/${quickShipOrder.id}/status`, 
        { 
          order_status: 'shipped',
          courier: courierValue,
          awb: quickShipForm.awb,
          estimated_delivery: ''
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local state
      setOrders(orders.map(o => o.id === quickShipOrder.id ? { 
        ...o, 
        order_status: 'shipped',
        courier: courierValue,
        awb: quickShipForm.awb
      } : o));
      
      toast.success('Comandă expediată! Email trimis către client.');
      setQuickShipOrder(null);
    } catch (error) {
      toast.error('Eroare la expediere');
    } finally {
      setQuickShipping(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiat în clipboard');
  };

  const openAddProduct = () => {
    setEditingProduct(null);
    setProductForm(emptyProduct);
    setShowProductModal(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      slug: product.slug || '',
      category: product.category || 'yeezy-350-v2',
      price: product.price || 0,
      original_price: product.original_price || 0,
      description: product.description || '',
      images: product.images?.length ? product.images : [''],
      sizes: (product.sizes || ['40', '41', '42', '43', '44']).map(s => String(s)),
      colors: product.colors || ['White', 'Black'],
      badge: product.badge || 'none',
      is_reflective: product.is_reflective || false,
      featured: product.featured || false,
      in_stock: product.in_stock !== false,
    });
    setShowProductModal(true);
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleProductFormChange = (field, value) => {
    setProductForm(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug from name
      if (field === 'name') {
        updated.slug = generateSlug(value);
      }
      // When category changes, reset sizes to appropriate defaults if current sizes don't match new category
      if (field === 'category') {
        const wasYeezy = isYeezyCategory(prev.category);
        const isNowYeezy = isYeezyCategory(value);
        if (wasYeezy !== isNowYeezy) {
          // Category type changed, reset to default sizes for new category
          updated.sizes = isNowYeezy 
            ? ['40', '40 2/3', '41 1/3', '42', '42 2/3'] 
            : ['40', '41', '42', '43', '44'];
        }
      }
      return updated;
    });
  };

  const handleImageChange = (index, value) => {
    const newImages = [...productForm.images];
    newImages[index] = value;
    setProductForm(prev => ({ ...prev, images: newImages }));
  };

  const addImageField = () => {
    setProductForm(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const removeImageField = (index) => {
    if (productForm.images.length > 1) {
      const newImages = productForm.images.filter((_, i) => i !== index);
      setProductForm(prev => ({ ...prev, images: newImages }));
    }
  };

  const toggleSize = (size) => {
    const sizeStr = String(size);
    const currentSizes = productForm.sizes.map(s => String(s));
    const availableSizes = getSizesForCategory(productForm.category);
    
    const newSizes = currentSizes.includes(sizeStr)
      ? currentSizes.filter(s => s !== sizeStr)
      : [...currentSizes, sizeStr];
    
    // Sort by the order in availableSizes array
    const sortedSizes = newSizes.sort((a, b) => {
      const indexA = availableSizes.indexOf(a);
      const indexB = availableSizes.indexOf(b);
      return indexA - indexB;
    });
    
    setProductForm(prev => ({ ...prev, sizes: sortedSizes }));
  };

  const saveProduct = async () => {
    // Validation
    if (!productForm.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!productForm.slug.trim()) {
      toast.error('Slug is required');
      return;
    }
    if (productForm.price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    if (productForm.images.filter(img => img.trim()).length === 0) {
      toast.error('At least one image URL is required');
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...productForm,
        images: productForm.images.filter(img => img.trim()),
        price: parseFloat(productForm.price),
        original_price: parseFloat(productForm.original_price) || parseFloat(productForm.price),
        badge: productForm.badge === 'none' ? '' : productForm.badge,
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload, { headers });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers });
        toast.success('Product created successfully');
      }

      setShowProductModal(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save product';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      confirmed: 'bg-blue-500/20 text-blue-500',
      processing: 'bg-purple-500/20 text-purple-500',
      shipped: 'bg-cyan-500/20 text-cyan-500',
      delivered: 'bg-green-500/20 text-green-500',
      cancelled: 'bg-red-500/20 text-red-500',
    };
    return colors[status] || 'bg-white/10 text-white/50';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#c00000] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight uppercase text-white" data-testid="admin-title">
              ADMIN DASHBOARD
            </h1>
            <Button
              onClick={openAddProduct}
              className="bg-[#c00000] hover:bg-[#a00000] text-white rounded-none font-heading tracking-wider"
              data-testid="add-product-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              ADD PRODUCT
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <div className="bg-[#121212] border border-white/5 p-6" data-testid="stat-revenue">
                <DollarSign className="w-8 h-8 text-[#c00000] mb-4" />
                <p className="font-body text-xs text-white/50 uppercase tracking-wider">Revenue</p>
                <p className="font-heading font-bold text-2xl text-white mt-1">
                  {stats.total_revenue.toFixed(0)} RON
                </p>
              </div>
              <div className="bg-[#121212] border border-white/5 p-6" data-testid="stat-orders">
                <ShoppingBag className="w-8 h-8 text-[#c00000] mb-4" />
                <p className="font-body text-xs text-white/50 uppercase tracking-wider">Orders</p>
                <p className="font-heading font-bold text-2xl text-white mt-1">{stats.total_orders}</p>
              </div>
              <div className="bg-[#121212] border border-white/5 p-6" data-testid="stat-products">
                <Package className="w-8 h-8 text-[#c00000] mb-4" />
                <p className="font-body text-xs text-white/50 uppercase tracking-wider">Products</p>
                <p className="font-heading font-bold text-2xl text-white mt-1">{stats.total_products}</p>
              </div>
              <div className="bg-[#121212] border border-white/5 p-6" data-testid="stat-users">
                <Users className="w-8 h-8 text-[#c00000] mb-4" />
                <p className="font-body text-xs text-white/50 uppercase tracking-wider">Users</p>
                <p className="font-heading font-bold text-2xl text-white mt-1">{stats.total_users}</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="products" className="w-full">
            <TabsList className="bg-[#121212] border border-white/5 p-1 mb-8">
              <TabsTrigger 
                value="products"
                className="font-heading tracking-wider data-[state=active]:bg-[#c00000] data-[state=active]:text-white rounded-none"
                data-testid="tab-products"
              >
                PRODUCTS
              </TabsTrigger>
              <TabsTrigger 
                value="orders" 
                className="font-heading tracking-wider data-[state=active]:bg-[#c00000] data-[state=active]:text-white rounded-none"
                data-testid="tab-orders"
              >
                ORDERS
              </TabsTrigger>
              <TabsTrigger 
                value="recommended" 
                className="font-heading tracking-wider data-[state=active]:bg-[#c00000] data-[state=active]:text-white rounded-none"
                data-testid="tab-recommended"
              >
                RECOMANDATE
              </TabsTrigger>
            </TabsList>

            {/* Products Tab */}
            <TabsContent value="products">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.02 }}
                    className={`bg-[#121212] border p-4 group ${product.in_stock ? 'border-white/5' : 'border-red-500/30'}`}
                    data-testid={`admin-product-${idx}`}
                  >
                    <div className="aspect-square bg-[#0a0a0a] mb-4 overflow-hidden relative">
                      <img 
                        src={product.images?.[0] || 'https://via.placeholder.com/400'} 
                        alt={product.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400?text=No+Image'; }}
                      />
                      {product.badge && (
                        <span className="absolute top-2 left-2 bg-[#c00000] text-white text-[10px] font-bold px-2 py-1">
                          {product.badge}
                        </span>
                      )}
                      {!product.in_stock && (
                        <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                          <span className="text-red-500 font-bold">OUT OF STOCK</span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-sm text-white truncate" title={product.name}>
                      {product.name}
                    </h3>
                    <p className="font-body text-xs text-white/50 mt-1">{CATEGORIES.find(c => c.value === product.category)?.label || product.category}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="font-heading font-bold text-[#c00000]">{product.price} RON</span>
                      {product.original_price > product.price && (
                        <span className="font-body text-xs text-white/30 line-through">{product.original_price} RON</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditProduct(product)}
                        className="flex-1 rounded-none text-xs border-white/10 hover:bg-white/5"
                        data-testid={`edit-product-${idx}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteProduct(product.id)}
                        className="rounded-none text-xs border-red-500/30 text-red-500 hover:bg-red-500/10"
                        data-testid={`delete-product-${idx}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {products.length === 0 && (
                <div className="bg-[#121212] border border-white/5 p-12 text-center">
                  <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-white mb-2">No products yet</h3>
                  <p className="font-body text-white/50 mb-6">Add your first product to get started</p>
                  <Button onClick={openAddProduct} className="bg-[#c00000] hover:bg-[#a00000] rounded-none">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              {/* Filters and Search Bar */}
              <div className="bg-[#121212] border border-white/5 p-4 mb-4">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                  {/* Status Filters */}
                  <div className="flex flex-wrap gap-2">
                    {ORDER_STATUSES.map(status => (
                      <button
                        key={status.value}
                        onClick={() => setOrderStatusFilter(status.value)}
                        className={`px-4 py-2 text-xs font-heading tracking-wider transition-all ${
                          orderStatusFilter === status.value 
                            ? `${status.color} border border-current` 
                            : 'bg-white/5 text-white/50 hover:bg-white/10'
                        }`}
                        data-testid={`filter-${status.value}`}
                      >
                        {status.label}
                        <span className="ml-2 opacity-70">({orderCounts[status.value]})</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Search */}
                  <div className="relative w-full lg:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      type="text"
                      placeholder="Caută nume, telefon, comandă..."
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      className="pl-10 bg-[#0a0a0a] border-white/10 text-white rounded-none h-10"
                      data-testid="order-search-input"
                    />
                  </div>
                </div>
              </div>

              {filteredOrders.length === 0 ? (
                <div className="bg-[#121212] border border-white/5 p-12 text-center">
                  <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-white mb-2">
                    {orderSearch ? 'Nicio comandă găsită' : 'Nu există comenzi'}
                  </h3>
                  <p className="font-body text-white/50">
                    {orderSearch ? 'Încearcă alte criterii de căutare' : 'Comenzile vor apărea aici'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Orders Table */}
                  <div className="bg-[#121212] border border-white/5 overflow-hidden">
                    {/* Table Header */}
                    <div className="hidden lg:grid grid-cols-12 gap-4 px-4 py-3 bg-[#0a0a0a] border-b border-white/5 text-xs font-heading text-white/50 uppercase tracking-wider">
                      <div className="col-span-2">Comandă</div>
                      <div className="col-span-2">Client</div>
                      <div className="col-span-1">Telefon</div>
                      <div className="col-span-1">Oraș</div>
                      <div className="col-span-1 text-right">Total</div>
                      <div className="col-span-1 text-center">Status</div>
                      <div className="col-span-2">Expediere</div>
                      <div className="col-span-2 text-right">Acțiuni</div>
                    </div>
                    
                    {/* Table Rows */}
                    <div className="divide-y divide-white/5">
                      {filteredOrders.map((order, idx) => (
                        <motion.div
                          key={order.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.02 }}
                          className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 py-4 hover:bg-white/[0.02] transition-colors items-center"
                          data-testid={`admin-order-${idx}`}
                        >
                          {/* Order ID & Date */}
                          <div className="col-span-2">
                            <div className="flex items-center gap-2 lg:block">
                              <span className="font-heading text-sm text-[#c00000]">
                                {order.order_number || `#${order.id.slice(0, 8)}`}
                              </span>
                              <button 
                                onClick={() => copyToClipboard(order.order_number || order.id.slice(0, 8))}
                                className="lg:hidden text-white/30 hover:text-white"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-xs text-white/40 mt-1">
                              {new Date(order.created_at).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          
                          {/* Customer Name */}
                          <div className="col-span-2">
                            <p className="font-body text-sm text-white truncate">{order.address?.full_name || 'N/A'}</p>
                            <p className="text-xs text-white/40 lg:hidden">{order.address?.phone}</p>
                          </div>
                          
                          {/* Phone */}
                          <div className="hidden lg:block col-span-1">
                            <a href={`tel:${order.address?.phone}`} className="text-xs text-white/70 hover:text-[#c00000]">
                              {order.address?.phone}
                            </a>
                          </div>
                          
                          {/* City */}
                          <div className="hidden lg:block col-span-1">
                            <p className="text-xs text-white/70 truncate">{order.address?.city}</p>
                          </div>
                          
                          {/* Total */}
                          <div className="col-span-1 lg:text-right">
                            <span className="font-heading text-sm text-white">{order.total?.toFixed(0)} RON</span>
                            <Badge className={`ml-2 lg:hidden rounded-none text-[10px] ${order.payment_method === 'cod' ? 'bg-orange-500/20 text-orange-500' : 'bg-green-500/20 text-green-500'}`}>
                              {order.payment_method === 'cod' ? 'COD' : 'CARD'}
                            </Badge>
                          </div>
                          
                          {/* Status */}
                          <div className="col-span-1 lg:text-center">
                            <Badge className={`${getStatusColor(order.order_status)} rounded-none font-heading text-[10px]`}>
                              {order.order_status === 'pending' && 'PENDING'}
                              {order.order_status === 'confirmed' && 'CONFIRMAT'}
                              {order.order_status === 'processing' && 'PREGĂTIRE'}
                              {order.order_status === 'shipped' && 'EXPEDIAT'}
                              {order.order_status === 'in_transit' && 'TRANZIT'}
                              {order.order_status === 'delivered' && 'LIVRAT'}
                              {order.order_status === 'cancelled' && 'ANULAT'}
                            </Badge>
                          </div>
                          
                          {/* Shipping Info */}
                          <div className="col-span-2">
                            {order.courier ? (
                              <div>
                                <p className="text-xs text-cyan-500 font-medium">{order.courier}</p>
                                {order.awb && (
                                  <p className="text-[10px] text-white/50 font-mono">{order.awb}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-white/30">-</span>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="col-span-2 flex items-center justify-end gap-2">
                            {/* Quick Ship Button - only for pending/confirmed orders */}
                            {['pending', 'confirmed', 'processing'].includes(order.order_status) && (
                              <Button
                                size="sm"
                                onClick={() => openQuickShip(order)}
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-none text-xs px-3 h-8"
                                data-testid={`quick-ship-${idx}`}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Ship
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openOrderWithTracking(order)}
                              className="rounded-none border-white/10 h-8 px-3"
                              data-testid={`view-order-${idx}`}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Results Count */}
                  <p className="text-xs text-white/30 mt-4 text-right">
                    {filteredOrders.length} {filteredOrders.length === 1 ? 'comandă' : 'comenzi'}
                    {orderStatusFilter !== 'all' && ` (${ORDER_STATUSES.find(s => s.value === orderStatusFilter)?.label})`}
                  </p>
                </>
              )}
            </TabsContent>

            {/* Recommended Products Tab */}
            <TabsContent value="recommended">
              <RecommendedProductsManager 
                products={products} 
                token={token}
                onUpdate={() => fetchData()}
              />
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Product Modal */}
      <AnimatePresence>
        {showProductModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setShowProductModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#121212] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="product-modal"
            >
              <div className="sticky top-0 bg-[#121212] border-b border-white/10 px-6 py-4 flex items-center justify-between">
                <h2 className="font-heading font-bold text-xl text-white">
                  {editingProduct ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
                </h2>
                <button onClick={() => setShowProductModal(false)} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                      Product Name *
                    </label>
                    <Input
                      value={productForm.name}
                      onChange={(e) => handleProductFormChange('name', e.target.value)}
                      placeholder="e.g., Yeezy 350 V2 Beluga"
                      className="bg-[#0a0a0a] border-white/10 text-white rounded-none"
                      data-testid="product-name-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                      Slug (URL) *
                    </label>
                    <Input
                      value={productForm.slug}
                      onChange={(e) => handleProductFormChange('slug', e.target.value)}
                      placeholder="yeezy-350-v2-beluga"
                      className="bg-[#0a0a0a] border-white/10 text-white rounded-none"
                      data-testid="product-slug-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                      Category *
                    </label>
                    <Select
                      value={productForm.category}
                      onValueChange={(value) => handleProductFormChange('category', value)}
                    >
                      <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-white rounded-none" data-testid="product-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                      Price (RON) *
                    </label>
                    <Input
                      type="number"
                      value={productForm.price}
                      onChange={(e) => handleProductFormChange('price', e.target.value)}
                      placeholder="699"
                      className="bg-[#0a0a0a] border-white/10 text-white rounded-none"
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                      Original Price (RON)
                    </label>
                    <Input
                      type="number"
                      value={productForm.original_price}
                      onChange={(e) => handleProductFormChange('original_price', e.target.value)}
                      placeholder="899"
                      className="bg-[#0a0a0a] border-white/10 text-white rounded-none"
                      data-testid="product-original-price-input"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    Description
                  </label>
                  <Textarea
                    value={productForm.description}
                    onChange={(e) => handleProductFormChange('description', e.target.value)}
                    placeholder="Premium quality sneakers..."
                    rows={3}
                    className="bg-[#0a0a0a] border-white/10 text-white rounded-none resize-none"
                    data-testid="product-description-input"
                  />
                </div>

                {/* Images */}
                <div>
                  <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    <ImageIcon className="w-4 h-4 inline mr-2" />
                    Image URLs *
                  </label>
                  <div className="space-y-2">
                    {productForm.images.map((img, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={img}
                          onChange={(e) => handleImageChange(idx, e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="flex-1 bg-[#0a0a0a] border-white/10 text-white rounded-none"
                          data-testid={`product-image-input-${idx}`}
                        />
                        {productForm.images.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeImageField(idx)}
                            className="rounded-none border-red-500/30 text-red-500 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addImageField}
                      className="w-full rounded-none border-white/10 text-white/50 hover:text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Another Image
                    </Button>
                  </div>
                  {/* Image Preview */}
                  {productForm.images.filter(i => i.trim()).length > 0 && (
                    <div className="mt-4 flex gap-2 flex-wrap">
                      {productForm.images.filter(i => i.trim()).map((img, idx) => (
                        <div key={idx} className="w-20 h-20 bg-[#0a0a0a] overflow-hidden">
                          <img 
                            src={img} 
                            alt={`Preview ${idx + 1}`} 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sizes */}
                <div>
                  <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    Available Sizes {isYeezyCategory(productForm.category) && <span className="text-[#c00000]">(Yeezy EU Sizing)</span>}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {getSizesForCategory(productForm.category).map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        className={`min-w-[3.5rem] px-2 h-10 font-heading text-sm transition-colors ${
                          productForm.sizes.map(s => String(s)).includes(String(size))
                            ? 'bg-[#c00000] text-white'
                            : 'bg-[#0a0a0a] border border-white/10 text-white/50 hover:border-white/30'
                        }`}
                        data-testid={`size-toggle-${size}`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Badge & Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                      Badge
                    </label>
                    <Select
                      value={productForm.badge}
                      onValueChange={(value) => handleProductFormChange('badge', value)}
                    >
                      <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-white rounded-none" data-testid="product-badge-select">
                        <SelectValue placeholder="No badge" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {BADGES.map(badge => (
                          <SelectItem key={badge || 'none'} value={badge}>{badge || 'No Badge'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="font-body text-sm text-white">Featured Product</label>
                      <Switch
                        checked={productForm.featured}
                        onCheckedChange={(checked) => handleProductFormChange('featured', checked)}
                        data-testid="product-featured-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="font-body text-sm text-white">Reflective</label>
                      <Switch
                        checked={productForm.is_reflective}
                        onCheckedChange={(checked) => handleProductFormChange('is_reflective', checked)}
                        data-testid="product-reflective-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="font-body text-sm text-white">In Stock</label>
                      <Switch
                        checked={productForm.in_stock}
                        onCheckedChange={(checked) => handleProductFormChange('in_stock', checked)}
                        data-testid="product-stock-switch"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-[#121212] border-t border-white/10 px-6 py-4 flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => setShowProductModal(false)}
                  className="rounded-none border-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveProduct}
                  disabled={saving}
                  className="bg-[#c00000] hover:bg-[#a00000] text-white rounded-none font-heading tracking-wider min-w-[120px]"
                  data-testid="save-product-btn"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingProduct ? 'Update' : 'Create'}
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Ship Modal */}
      <AnimatePresence>
        {quickShipOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setQuickShipOrder(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#121212] border border-white/10 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="quick-ship-modal"
            >
              <div className="bg-cyan-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Send className="w-5 h-5 text-white" />
                    <h2 className="font-heading font-bold text-lg text-white">EXPEDIERE RAPIDĂ</h2>
                  </div>
                  <button onClick={() => setQuickShipOrder(null)} className="text-white/70 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Order Info */}
                <div className="bg-[#0a0a0a] p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-white/50">Comandă</span>
                    <span className="text-sm font-heading text-[#c00000]">{quickShipOrder.order_number || `#${quickShipOrder.id.slice(0, 8)}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-white/50">Client</span>
                    <span className="text-sm text-white">{quickShipOrder.address?.full_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-white/50">Destinație</span>
                    <span className="text-sm text-white">{quickShipOrder.address?.city}, {quickShipOrder.address?.county}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-white/50">Total</span>
                    <span className="text-sm font-heading text-white">{quickShipOrder.total?.toFixed(0)} RON</span>
                  </div>
                </div>

                {/* Courier Selection */}
                <div>
                  <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    Curier *
                  </label>
                  <Select
                    value={quickShipForm.courier}
                    onValueChange={(value) => setQuickShipForm(prev => ({ ...prev, courier: value }))}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-white rounded-none" data-testid="quick-courier-select">
                      <SelectValue placeholder="Selectează curier" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      {COURIERS.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Courier Input */}
                {quickShipForm.courier === 'Other' && (
                  <div>
                    <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                      Nume Curier *
                    </label>
                    <Input
                      value={quickShipForm.customCourier}
                      onChange={(e) => setQuickShipForm(prev => ({ ...prev, customCourier: e.target.value }))}
                      placeholder="ex: DPD, GLS..."
                      className="bg-[#0a0a0a] border-white/10 text-white rounded-none"
                      data-testid="quick-custom-courier"
                    />
                  </div>
                )}

                {/* AWB Input */}
                <div>
                  <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    AWB / Număr Tracking *
                  </label>
                  <Input
                    value={quickShipForm.awb}
                    onChange={(e) => setQuickShipForm(prev => ({ ...prev, awb: e.target.value }))}
                    placeholder="ex: 1234567890"
                    className="bg-[#0a0a0a] border-white/10 text-white rounded-none font-mono"
                    data-testid="quick-awb-input"
                  />
                </div>

                {/* Info */}
                <div className="bg-cyan-500/10 border border-cyan-500/20 p-3">
                  <p className="text-xs text-cyan-400">
                    Clientul va primi automat email cu informațiile de tracking.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setQuickShipOrder(null)}
                    className="flex-1 rounded-none border-white/10"
                  >
                    Anulează
                  </Button>
                  <Button
                    onClick={handleQuickShip}
                    disabled={quickShipping || !quickShipForm.courier || !quickShipForm.awb || (quickShipForm.courier === 'Other' && !quickShipForm.customCourier)}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-none font-heading"
                    data-testid="confirm-ship-btn"
                  >
                    {quickShipping ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        EXPEDIAZĂ
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Order Detail Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setSelectedOrder(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#121212] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="order-detail-modal"
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#121212] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="font-heading font-bold text-xl text-white">
                    {selectedOrder.order_number || `#${selectedOrder.id.slice(0, 8)}`}
                  </h2>
                  <p className="text-xs text-white/40">
                    {new Date(selectedOrder.created_at).toLocaleString('ro-RO')}
                  </p>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Status Row */}
                <div className="flex flex-wrap items-center gap-3">
                  <Badge className={`${getStatusColor(selectedOrder.order_status)} rounded-none font-heading px-3 py-1`}>
                    {selectedOrder.order_status === 'pending' && 'ÎN AȘTEPTARE'}
                    {selectedOrder.order_status === 'confirmed' && 'CONFIRMAT'}
                    {selectedOrder.order_status === 'processing' && 'ÎN PREGĂTIRE'}
                    {selectedOrder.order_status === 'shipped' && 'EXPEDIAT'}
                    {selectedOrder.order_status === 'in_transit' && 'ÎN TRANZIT'}
                    {selectedOrder.order_status === 'delivered' && 'LIVRAT'}
                    {selectedOrder.order_status === 'cancelled' && 'ANULAT'}
                  </Badge>
                  <Badge className={`rounded-none font-heading px-3 py-1 ${selectedOrder.payment_method === 'card' || selectedOrder.payment_method === 'stripe' ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500'}`}>
                    {selectedOrder.payment_method === 'card' || selectedOrder.payment_method === 'stripe' ? 'CARD' : 'RAMBURS'}
                  </Badge>
                  <div className="flex-1" />
                  <span className="font-heading text-2xl text-[#c00000]">{selectedOrder.total?.toFixed(0)} RON</span>
                </div>

                {/* Quick Actions for unshipped orders */}
                {['pending', 'confirmed', 'processing'].includes(selectedOrder.order_status) && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-heading text-sm text-cyan-400">Pregătit pentru expediere?</p>
                        <p className="text-xs text-white/50 mt-1">Adaugă AWB și trimite email clientului</p>
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedOrder(null);
                          openQuickShip(selectedOrder);
                        }}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-none font-heading"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        EXPEDIAZĂ
                      </Button>
                    </div>
                  </div>
                )}

                {/* Customer Info Card */}
                <div className="bg-[#0a0a0a] p-4">
                  <h3 className="font-heading text-xs text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Client
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-body text-white font-medium">{selectedOrder.address?.full_name}</p>
                      <a href={`tel:${selectedOrder.address?.phone}`} className="font-body text-sm text-[#c00000] hover:underline flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" />
                        {selectedOrder.address?.phone}
                      </a>
                      {(selectedOrder.address?.email || selectedOrder.guest_email) && (
                        <p className="font-body text-xs text-white/50 mt-1">
                          {selectedOrder.address?.email || selectedOrder.guest_email}
                        </p>
                      )}
                    </div>
                    <div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-white/30 mt-0.5 flex-shrink-0" />
                        <p className="font-body text-sm text-white/70">
                          {selectedOrder.address?.address}<br />
                          {selectedOrder.address?.city}, {selectedOrder.address?.county}<br />
                          {selectedOrder.address?.postal_code}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Management */}
                <div className="bg-[#0a0a0a] p-4 border border-white/10">
                  <h3 className="font-heading text-xs text-white/40 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Informații Expediere
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="font-body text-xs text-white/50 mb-2 block">Status</label>
                      <Select
                        value={trackingForm.order_status}
                        onValueChange={(value) => setTrackingForm(prev => ({ ...prev, order_status: value }))}
                      >
                        <SelectTrigger className="bg-[#121212] border-white/10 text-white rounded-none h-10" data-testid="order-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          <SelectItem value="pending">În Așteptare</SelectItem>
                          <SelectItem value="confirmed">Confirmată</SelectItem>
                          <SelectItem value="processing">În Pregătire</SelectItem>
                          <SelectItem value="shipped">Expediată</SelectItem>
                          <SelectItem value="in_transit">În Tranzit</SelectItem>
                          <SelectItem value="delivered">Livrată</SelectItem>
                          <SelectItem value="cancelled">Anulată</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="font-body text-xs text-white/50 mb-2 block">Curier</label>
                      <Select
                        value={trackingForm.courier || "none"}
                        onValueChange={(value) => setTrackingForm(prev => ({ ...prev, courier: value === "none" ? "" : value }))}
                      >
                        <SelectTrigger className="bg-[#121212] border-white/10 text-white rounded-none h-10" data-testid="order-courier-select">
                          <SelectValue placeholder="Selectează" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          <SelectItem value="none">Neselectat</SelectItem>
                          {COURIERS.map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="font-body text-xs text-white/50 mb-2 block">AWB / Tracking</label>
                      <Input
                        value={trackingForm.awb}
                        onChange={(e) => setTrackingForm(prev => ({ ...prev, awb: e.target.value }))}
                        placeholder="Număr AWB"
                        className="bg-[#121212] border-white/10 text-white rounded-none h-10 font-mono"
                        data-testid="order-awb-input"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs text-white/50 mb-2 block">Livrare Estimată (opțional)</label>
                      <Input
                        value={trackingForm.estimated_delivery}
                        onChange={(e) => setTrackingForm(prev => ({ ...prev, estimated_delivery: e.target.value }))}
                        placeholder="ex: 2-3 zile"
                        className="bg-[#121212] border-white/10 text-white rounded-none h-10"
                        data-testid="order-delivery-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={updateOrderWithTracking}
                      disabled={updatingOrder}
                      className="flex-1 bg-[#c00000] hover:bg-[#a00000] text-white rounded-none font-heading h-10"
                      data-testid="update-order-btn"
                    >
                      {updatingOrder ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salvează
                        </>
                      )}
                    </Button>
                    
                    {/* Mark as Shipped button */}
                    {['pending', 'confirmed', 'processing'].includes(trackingForm.order_status) && trackingForm.awb && trackingForm.courier && (
                      <Button
                        onClick={() => {
                          setTrackingForm(prev => ({ ...prev, order_status: 'shipped' }));
                          setTimeout(() => updateOrderWithTracking(), 100);
                        }}
                        disabled={updatingOrder}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-none font-heading h-10"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Marchează Expediat
                      </Button>
                    )}
                  </div>
                  
                  {trackingForm.order_status === 'shipped' && trackingForm.awb && (
                    <p className="text-xs text-green-500 mt-3 text-center">
                      Email de expediere va fi trimis automat la salvare
                    </p>
                  )}
                </div>

                {/* Order Items */}
                <div>
                  <h3 className="font-heading text-xs text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Produse ({selectedOrder.items?.length || 0})
                  </h3>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex gap-3 bg-[#0a0a0a] p-3">
                        <img 
                          src={item.product_image} 
                          alt={item.product_name}
                          className="w-14 h-14 object-cover rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-sm text-white truncate">{item.product_name}</p>
                          <p className="font-body text-xs text-white/50">
                            Mărime: EU {item.size} &bull; Qty: {item.quantity}
                          </p>
                        </div>
                        <p className="font-heading text-sm text-[#c00000]">{item.price} RON</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
